import React, { useEffect } from 'react';
import { cx } from 'cva';
import Typography from '@ui/Typography';
import { useAddOrUpdateProject } from '@/actions/feed.actions';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, Tab, Tabs } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { CampaignType } from '../../constants/postType.constants';
import StyledContainedButton from '../Styled/StyledContainedButton';
import CampaignProjectForm from './CampaignProjectForm';
import { defaultProjectFormValues, projectSchema } from './projectFormSchema';
import PostImageSelector from '@components/PostImageSelector/PostImageSelector';
import PostVideoSelector from '@components/PostVideoSelector/PostVideoSelector';
import { MdPhoto } from 'react-icons/md';
import { IoMdVideocam } from 'react-icons/io';
import { isEmpty, size, pick } from 'lodash-es';
import { PublishToPublicPage } from 'components/PublishToPublicPage';
import useUserProfile from '@/hooks/useUserProfile';
import { TargetViewers } from '@/constants/profileType.constant';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import ProjectSuccessDialog from '../ActivitySuccessDialog/ProjectSuccessDialog/ProjectSuccessDialog';

function CreateProjectDialogTemplate(props) {
  const {
    open,
    post = null,
    type = CampaignType.FundRaise,
    onClose,
    updateHeader,
    handleBack,
  } = props;

  const profile = useUserProfile();

  const [tabType, setTabType] = React.useState(type);
  const [step, setStep] = React.useState('form');
  const [rewardEarned, setRewardEarned] = React.useState(0);
  const [createdPost, setCreatedPost] = React.useState(null);
  const [submitted, setSubmitted] = React.useState(false);
  const [selectedImages, setSelectedImages] = React.useState(null);
  const [selectedVideos, setSelectedVideos] = React.useState(null);
  const [showNoImagesError, setShowNoImagesError] = React.useState(false);
  const [showNoVideosError, setShowNoVideosError] = React.useState(false);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: defaultProjectFormValues(),
    resolver: yupResolver(projectSchema),
  });

  const {
    getValues,
    trigger,
    formState: { isValid },
  } = formMethods;

  const isEditMode = !!post;

  const {
    mutateAsync: addOrUpdatePorject,
    isSuccess,
    isError,
  } = useAddOrUpdateProject();

  useEffect(() => {
    if (submitted) {
      if (isSuccess) {
        setSubmitted(false);
      } else if (isError) {
        setSubmitted(false);
      }
    }
  }, [isSuccess, isError, submitted]);

  useEffect(() => {
    if (open) {
      setTabType(type);
      if (post) {
        formMethods.reset({
          ...defaultProjectFormValues({
            ...pick(post, [
              'postAs',
              'targetViewers',
              'targetOrgs',
              'isFeatured',
            ]),
            ...post.sourcePostData,
          }),
          campaignType: type,
        });
      } else {
        formMethods.reset({
          ...defaultProjectFormValues(),
          campaignType: type,
        });
      }
    }
  }, [open, type]);

  useEffect(() => {
    getTitle();
  }, [step]);

  const handleTabChange = (newValue) => {
    setTabType(newValue);
    formMethods.reset({
      ...defaultProjectFormValues(),
      campaignType: newValue,
    });
    setSelectedImages(null);
    setSelectedVideos(null);
  };

  const handleClose = () => {
    onClose();
    setSubmitted(false);
    setStep('form');
    formMethods.reset(defaultProjectFormValues());
    setSelectedImages(null);
    setSelectedVideos(null);
    setShowNoImagesError(false);
    setShowNoVideosError(false);
  };

  const handleSubmit = async () => {
    if (step === 'form') {
      if (isValid) {
        if (size(selectedImages) > 0 && size(selectedVideos) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else if (size(selectedImages) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else if (size(selectedVideos) > 0) {
          setStep('publish');
          setShowNoImagesError(false);
          setShowNoVideosError(false);
        } else {
          setShowNoImagesError(true);
          setShowNoVideosError(true);
        }
      } else {
        trigger();
      }
    } else {
      setSubmitted(true);
      const formValues = getValues();
      const data = await addOrUpdatePorject({
        formValues,
        post,
        images: selectedImages,
        videos: selectedVideos,
      });
      setRewardEarned(data?.reward);
      setCreatedPost(data?.post);
      setStep('success');
    }
  };

  const handleAddPictures = () => {
    setStep('pictures');
  };

  const handleAddVideos = () => {
    setStep('videos');
  };

  const updatePostImages = (images) => {
    setSelectedImages(images);
    if (!isEmpty(images)) {
      setSelectedVideos(null);
      setShowNoVideosError(false);
      setShowNoImagesError(false);
    }
  };

  const updatePostVideos = (videos) => {
    setSelectedVideos(videos);
    if (!isEmpty(videos)) {
      setSelectedImages(null);
      setShowNoVideosError(false);
      setShowNoImagesError(false);
    }
  };

  const handleImageDiscard = () => {
    setStep('form');
    setSelectedImages(null);
  };

  const handleVideoDiscard = () => {
    setStep('form');
    setSelectedVideos(null);
  };

  const getProjectTitle = () => {
    switch (tabType) {
      case CampaignType.FundRaise:
        return 'Fundraiser Project';
      case CampaignType.CollectGoods:
        return 'In-Kind Project';
      default:
        return '';
    }
  };

  const getTitle = () => {
    const projectTitle = getProjectTitle();
    let title = '';
    switch (step) {
      case 'pictures':
        title = 'Add Images';
        break;
      case 'videos':
        title = 'Add Videos';
        break;
      case 'success':
        title = '';
        break;
      default:
        title = isEditMode ? `Edit ${projectTitle}` : `Create ${projectTitle}`;
        break;
    }

    updateHeader(title);
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div
        className={`flex w-full flex-col-reverse gap-4 overflow-y-auto overflow-x-hidden lg:flex-row ${
          isLessThanSmallScreen ? '' : 'flex-1'
        }`}>
        <div className={cx(step !== 'form' && 'hidden', 'w-full pr-[5px]')}>
          <div className="mt-3 w-full">
            <FormProvider {...formMethods}>
              <div
                className={cx(step !== 'form' && 'hidden', 'w-full pr-[5px]')}>
                <CampaignProjectForm
                  images={selectedImages}
                  videos={selectedVideos}
                  edit={isEditMode}
                />
              </div>
              {/* {step === 'publish' && (
                  <div className="flex-1">
                    <PublishToPublicPage />
                  </div>
                )} */}
            </FormProvider>
          </div>
        </div>
        {step === 'pictures' && (
          <PostImageSelector
            onChange={updatePostImages}
            value={selectedImages}
          />
        )}
        {step === 'videos' && (
          <PostVideoSelector
            onChange={updatePostVideos}
            value={selectedVideos}
          />
        )}
      </div>

      <div
        className={`flex-rows flex w-full justify-end gap-4 pt-4 ${
          isLessThanSmallScreen ? 'mb-32' : ''
        }`}>
        {step === 'form' && (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              <IconButton onClick={handleAddPictures}>
                <MdPhoto
                  className={`${
                    showNoImagesError ? 'animate-pulse text-error' : ''
                  }`}
                />
              </IconButton>

              <IconButton onClick={handleAddVideos}>
                <IoMdVideocam
                //   className={`${
                //     showNoVideosError ? 'animate-pulse text-error' : ''
                //   }`}
                />
              </IconButton>

              {showNoImagesError && (
                <Typography color="error">Please upload an image</Typography>
              )}

              {/* {showNoVideosError && (
                  <Typography color="error">Please upload a video</Typography>
                )} */}
            </div>

            <div className="ml-auto flex gap-4">
              <StyledContainedButton
                type="button"
                invertColors
                onClick={handleBack}>
                Back
              </StyledContainedButton>

              <StyledContainedButton
                type="submit"
                loading={submitted}
                onClick={handleSubmit}>
                {isEditMode ? 'Update' : 'Next'}
              </StyledContainedButton>
            </div>
          </div>
        )}
        {step === 'pictures' && (
          <div className="flex gap-2">
            <StyledContainedButton
              type="button"
              invertColors
              onClick={handleImageDiscard}>
              Back
            </StyledContainedButton>
            <StyledContainedButton
              type="button"
              disabled={isEmpty(selectedImages)}
              onClick={() => setStep('form')}>
              Done
            </StyledContainedButton>
          </div>
        )}
        {step === 'videos' && (
          <div className="flex gap-2">
            <StyledContainedButton
              type="button"
              invertColors
              onClick={handleVideoDiscard}>
              Back
            </StyledContainedButton>
            <StyledContainedButton
              type="button"
              disabled={isEmpty(selectedVideos)}
              onClick={() => setStep('form')}>
              Done
            </StyledContainedButton>
          </div>
        )}

        {step === 'publish' && (
          <div
            className={`flex flex-1 flex-col gap-4 ${
              isLessThanSmallScreen ? 'mb-32' : ''
            }`}>
            <div className="flex-1">
              <FormProvider {...formMethods}>
                <PublishToPublicPage />
              </FormProvider>
            </div>

            <div className="ml-auto">
              <StyledContainedButton
                type="button"
                loading={submitted}
                onClick={handleSubmit}>
                Publish
              </StyledContainedButton>
            </div>
          </div>
        )}

        {step === 'success' && (
          <ProjectSuccessDialog
            isPost
            type={type}
            user={profile}
            post={createdPost}
            onClose={handleClose}
            reward={rewardEarned}
            isPostPublic={createdPost?.targetViewers === TargetViewers.All}
          />
        )}
      </div>
    </>
  );
}

export default CreateProjectDialogTemplate;
